/* eslint-disable no-console */
import { BroadcastChannel } from 'broadcast-channel';
import Cookies from 'js-cookie';
import workerHost from './workerHost';
import * as StreamProtocol from './streamProtocol';
import AuthProxy from '../auth/hooks/AuthProxy';
import { PubSubMessage } from './types';
import MessageProxy from './MessageProxy';
import debounceNotificationSound from './debounceNotificationSound';
import { IMPERSONATION_COOKIE_NAME } from './constants';

const isOnlineMessage = (message: any): message is StreamProtocol.Online => (
  message?.type === 'online'
  && typeof message?.data !== 'undefined'
);

const isTypingMessage = (message: any): message is StreamProtocol.Typing => (
  message?.type === 'typing'
  && typeof message?.thread_id === 'string'
  && typeof message?.data === 'string'
);

const isNotificationMessage = (message: any): message is StreamProtocol.Notification => (
  message.type === 'notification'
  && typeof message?.thread_id === 'string'
  && typeof message?.message === 'string'
  && typeof message?.data === 'object'
  && typeof message?.data.content === 'string'
  && typeof message?.data.user_id === 'number'
  && typeof message?.data.message_id === 'number'
  && typeof message?.data.date_created === 'string'
);

const isUserMessage = (message: any): message is StreamProtocol.ThreadMessage => (
  message?.type === 'msg'
  && typeof message?.thread_id === 'string'
  && typeof message?.data === 'object'
  && typeof message?.data.content === 'string'
  && typeof message?.data.message_id === 'string'
  && typeof message?.data.date_created === 'string'
);

const isEditMessage = (message: any): message is StreamProtocol.EditMessage => (
  message?.type === 'edit'
  && typeof message?.thread_id === 'string'
  && typeof message?.data === 'object'
  && typeof message?.data.content === 'string'
  && typeof message?.data.message_id === 'string'
);

const isSeenMessage = (message: any): message is StreamProtocol.SeenMessage => (
  message?.type === 'seen'
  && typeof message?.thread_id === 'string'
  && typeof message?.ts === 'string'
  && typeof message?.ahoy_id === 'string'
  && typeof message?.ahoy_msgid === 'string'
  && typeof message?.message_id === 'string'
);

// Extend the window object with the `gmb` object
declare global {
  interface Window {
    gmb: GMB;
  }

  interface GMB {
    socketWorker?: Worker | undefined;
    workerHost?: boolean;
  }
}

type MessageFromWorker = MessageEvent<PubSubMessage> | MessageEvent<{ type: 'ready' }>;
const noop = () => { };

/**
 * Instantiate the pubsub worker creating code.
 * This is used in both the app and the pubsub iframe,
 * so we need to provide noop fallbacks for the pubsub iframe context.
 */
const initPubSub = async (
  updatePresence: (presence: Record<number, boolean>) => void = noop,
  clearPresence: () => void = noop,
  updateTypingIndicators: (message: StreamProtocol.Typing) => void = noop,
  addMessage: (message: StreamProtocol.Notification) => void = noop,
  addUserMessage: (message: StreamProtocol.ThreadMessage) => void = noop,
  editMessage: (message: StreamProtocol.EditMessage) => void = noop,
  markMessageAsSeen: (message: StreamProtocol.SeenMessage) => void = noop,
) => {
  let socketWorker: Worker | undefined;

  const broadcastChannel = new BroadcastChannel('messageProxy', {
    webWorkerSupport: false,
  });

  const messageProxy = new MessageProxy(broadcastChannel);

  const broadcastSocketSender = new BroadcastChannel('broadcastSocketSender', {
    webWorkerSupport: false,
  });

  const soundBroadcast = new BroadcastChannel('soundSocketSender', {
    webWorkerSupport: false,
  });

  /**
   * The socket worker is only instantiated by the tab that is promoted to the host.
   * If the current host tab is closed, another tab will be promoted to be the host.
   */
  workerHost.onPromotion(() => {
    if (__DEV__) {
      console.log('I\'m the worker host now');
    }

    // The socketWorker is created from the primary domain to work around
    // issues with older versions of Safari that don't support `worker-src`
    // Content Security Policy headers. The worker loader will load the pubsub
    // worker from the CDN Domain and make it available via
    // `window.gmb.socketWorker`.

    // The `workerHost` has to be set to `true` so that the worker can be
    // instantiated. This is to prevent accessing `window.gmb.socketWorker`
    // from creating worker instances across multiple tabs. A tab should
    // only create a pubsub worker instance once it is promoted to worker host.
    window.gmb.workerHost = true;
    socketWorker = window.gmb.socketWorker;

    // For debugging purposes, messages can be sent to the worker as follows.
    // `gmb.socketWorker.postMessage({ type: 'logout' })`
    // `gmb.socketWorker.postMessage({ type: 'login' })`

    broadcastSocketSender.addEventListener('message', message => {
      socketWorker?.postMessage(message);
    });

    soundBroadcast.addEventListener('message', () => {
      debounceNotificationSound();
    });

    socketWorker?.addEventListener?.('message', (event: MessageFromWorker) => {
      switch (event.data.type) {
        case 'ready':
          socketWorker?.postMessage({
            type: 'load',
            isImpersonating: !!Cookies.get(IMPERSONATION_COOKIE_NAME),
          });
          break;
        case 'message':
        case 'close':
          messageProxy.onWorkerMessage(event as MessageEvent<PubSubMessage>);
          break;
        default:
          break;
      }
    });
  });

  messageProxy.on('close', () => {
    clearPresence();
  });

  messageProxy.on('message', (data: PubSubMessage) => {
    const { timestamp, message } = data.data;
    if (__DEV__) {
      if (timestamp > '0') {
        console.log('NEW PUBSUB MESSAGE: ', timestamp, message);
      } else {
        console.log('SERVER MESSAGE: ', message);
      }
    }
    if (isOnlineMessage(message)) {
      updatePresence(message.data);
    }
    if (isTypingMessage(message)) {
      updateTypingIndicators(message);
    }
    if (isNotificationMessage(message)) {
      addMessage(message);
    }
    if (isUserMessage(message)) {
      addUserMessage(message);
    }
    if (isEditMessage(message)) {
      editMessage(message);
    }
    if (isSeenMessage(message)) {
      markMessageAsSeen(message);
    }
  });

  // Inform the socketWorker of authentication state changes so that it can
  // connect and disconnect accordingly.
  const authProxy = AuthProxy.getInstance();
  authProxy.on('login', () => socketWorker?.postMessage({ type: 'login' }));
  authProxy.on('logout', () => socketWorker?.postMessage({ type: 'logout' }));
  authProxy.on('impersonate', () => socketWorker?.postMessage({ type: 'impersonate' }));
};

export default initPubSub;
